import TitrePage from "../components/charte/TitrePage";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

import { useAchievementForm } from "../custom_forms/AchievementForm";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import {
  createAchievement,
  getAchievements,
  getAchievementsActive,
  getgifts,
  updateAchievements,
  updateAchievements1,
} from "../utilities/backofficeservice";
import { getUser } from "../utilities/adminservice";
import { Niveau, getNiveaux, majNiveau } from "../utilities/locationService";
import { getUserdetails, messageServeur } from "../utilities/userService";
import CommonDialog from "../commondialog/confirm";
import Gifts from "./gifts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import store from "../redux/store";
import { AIDE_RESEAU } from "../aide/textesAide";

// ─── Design System ────────────────────────────────────────────────────────────
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: 'var(--c-bg)',
  card: 'var(--c-surface)',
  border: 'var(--c-border)',
  borderAccent: '#1F1E1B',
  accent: '#1F1E1B',
  accentLight: '#57534A',
  accentMuted: 'rgba(31,30,27,0.07)',
  text: '#1F1E1B',
  textSub: '#57534A',
  textMuted: '#57534A',
  success: '#356A0F',
  warning: '#7C5600',
  error: '#A23A0D',
} as const;

/* Les niveaux viennent du référentiel serveur (GET /niveaux, décision D7) :
   noms, critères, avantages et activation. Seule la couleur reste ici, par
   numéro de niveau, parce qu'elle n'est qu'un choix d'affichage. */
const COULEURS_NIVEAU: Record<number, string> = {
  1: '#57534A',
  2: '#1D5FA4',
  3: '#1F1E1B',
  4: '#7C5600',
  5: '#356A0F',
};
const couleurNiveau = (n: number) => COULEURS_NIVEAU[n] ?? '#57534A';

/* Onglets de l'écran : la gestion des Tips offerts au palier y est intégrée
   (option 3), la route /gifts ouvre directement ce troisième onglet. */
export const ONGLET_NIVEAUX = 0;
export const ONGLET_BADGES = 1;
export const ONGLET_TIPS_OFFERTS = 2;

// ─── Shared MUI sx helpers ────────────────────────────────────────────────────
const fieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    background: 'rgba(31,30,27,0.03)',
    color: C.text,
    borderRadius: '8px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.borderAccent },
    '&.Mui-focused fieldset': { borderColor: C.accentLight },
  },
  '& .MuiInputLabel-root': { color: C.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: C.accentLight },
};

const selectSx = {
  background: 'rgba(31,30,27,0.03)',
  color: C.text,
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderAccent },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.accentLight },
  '& .MuiSvgIcon-root': { color: C.textMuted },
};


// ─── Types ────────────────────────────────────────────────────────────────────
interface ActiveAchievement {
  achievement_id: number;
  achievement_name: string;
  achievement_description: string;
  achievement_criteria: string;
  level: string;
  gift_name_name: string;
  /** Seuil : cumul de Tips gagnés (token_allocated) qui déclenche le palier. */
  no_of_tokens: string;
  /** Tips offerts par le cadeau lié, null s'il n'y en a pas (ou supprimé). */
  tips_offerts: number | null;
}

/* Supprimé = delete_status autre que « NO » : ni listé, ni compté. */
function nonSupprime(deleteStatus?: string | null): boolean {
  return deleteStatus == null || deleteStatus.trim().toUpperCase() === 'NO';
}

// ─── Édition d'un niveau ──────────────────────────────────────────────────────
interface NiveauDialogProps {
  niveau: Niveau | null;
  onClose: () => void;
  onSaved: () => void;
}

/* Les critères d'un niveau sont appliqués par le job de nuit : les changer
   change qui sera promu (ou non) dès la prochaine exécution. D'où le motif
   obligatoire, conservé au journal par le serveur. */
function NiveauDialog({ niveau, onClose, onSaved }: NiveauDialogProps) {
  const dispatch = useDispatch();
  const [nom, setNom] = useState('');
  const [avantages, setAvantages] = useState('');
  const [actif, setActif] = useState(true);
  const [minApprouves, setMinApprouves] = useState('');
  const [taux, setTaux] = useState('');
  const [anciennete, setAnciennete] = useState('');
  const [categories, setCategories] = useState('');
  const [motif, setMotif] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!niveau) return;
    setNom(niveau.nom ?? '');
    setAvantages(niveau.avantages ?? '');
    setActif(niveau.actif);
    setMinApprouves(niveau.minApprouves != null ? String(niveau.minApprouves) : '');
    setTaux(niveau.tauxApprobationMin != null ? String(niveau.tauxApprobationMin) : '');
    setAnciennete(niveau.ancienneteJoursMin != null ? String(niveau.ancienneteJoursMin) : '');
    setCategories(niveau.categoriesMin != null ? String(niveau.categoriesMin) : '');
    setMotif('');
  }, [niveau]);

  const entier = (v: string) => (v.trim() === '' ? null : Math.round(Number(v)));

  const enregistrer = async () => {
    if (!niveau) return;
    if (!motif.trim()) { dispatch(openSnackbar('Le motif est obligatoire.', 'red')); return; }
    const valeurs = [minApprouves, taux, anciennete, categories].map(entier);
    if (valeurs.some((v) => v != null && (isNaN(v) || v < 0))) {
      dispatch(openSnackbar('Les critères sont des nombres entiers positifs.', 'red')); return;
    }
    if (valeurs[1] != null && (valeurs[1] as number) > 100) {
      dispatch(openSnackbar("Le taux d'approbation est un pourcentage, au plus 100.", 'red')); return;
    }
    setBusy(true);
    try {
      await majNiveau(niveau.niveau, {
        nom: nom.trim(),
        avantages: avantages.trim(),
        actif: niveau.niveau === 1 ? true : actif,
        minApprouves: valeurs[0],
        tauxApprobationMin: valeurs[1],
        ancienneteJoursMin: valeurs[2],
        categoriesMin: valeurs[3],
        motif: motif.trim(),
      });
      dispatch(openSnackbar(`Niveau ${niveau.niveau} enregistré.`, 'green'));
      onSaved();
      onClose();
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Enregistrement du niveau impossible.'), 'red'));
    } finally {
      setBusy(false);
    }
  };

  const champ = (label: string, value: string, set: (v: string) => void, aide?: string, type = 'number') => (
    <Box>
      <TextField
        fullWidth size="small" label={label} type={type} value={value}
        onChange={(e) => set(e.target.value)}
        inputProps={type === 'number' ? { min: 0, step: 1 } : undefined}
        sx={fieldSx}
      />
      {aide && <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.5 }}>{aide}</Typography>}
    </Box>
  );

  return (
    <Dialog
      open={!!niveau}
      onClose={() => { if (!busy) onClose(); }}
      PaperProps={{ sx: { background: C.card, border: `1px solid ${C.borderAccent}`, borderRadius: '8px', color: C.text, width: 520 } }}
    >
      <DialogTitle sx={{ fontSize: 17, fontWeight: 700, color: C.text }}>
        Modifier le niveau {niveau?.niveau}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {champ('Nom', nom, setNom, undefined, 'text')}
        {champ('Avantages', avantages, setAvantages, 'Ce que le niveau ouvre au Spotter, tel que l’app l’affiche', 'text')}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {champ('Lieux validés (min.)', minApprouves, setMinApprouves)}
          {champ("Taux d'approbation (min. %)", taux, setTaux)}
          {champ('Ancienneté (min. jours)', anciennete, setAnciennete)}
          {champ('Catégories distinctes (min.)', categories, setCategories)}
        </Box>
        <FormControlLabel
          sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 13, color: C.textSub } }}
          control={
            <Switch
              size="small" checked={niveau?.niveau === 1 ? true : actif}
              disabled={niveau?.niveau === 1}
              onChange={(e) => setActif(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: C.success },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { background: C.success },
              }}
            />
          }
          label={niveau?.niveau === 1
            ? 'Actif — le niveau 1 ne peut pas être désactivé'
            : 'Actif — un niveau inactif n’est jamais attribué par le job de nuit'}
        />
        {champ('Motif (obligatoire)', motif, setMotif, 'Conservé au journal avec la modification', 'text')}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose} disabled={busy}
          sx={{ color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
        >
          Annuler
        </Button>
        <Button
          onClick={enregistrer} disabled={busy || !motif.trim()}
          sx={{
            background: C.accent, color: '#fff',
            borderRadius: '8px', textTransform: 'none', fontWeight: 600,
            '&.Mui-disabled': { color: '#57534A', opacity: 0.6 },
          }}
        >
          {busy ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function Achievements({ ongletInitial = ONGLET_NIVEAUX }: { ongletInitial?: number } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  const { formValues, setFormValues } = useAchievementForm();

  // ── UI state ─────────────────────────────────────────────────────────────
  const [tab, setTab] = useState(ongletInitial);
  useEffect(() => { setTab(ongletInitial); }, [ongletInitial]);
  const [expandedLevel, setExpandedLevel] = useState<number | null>(null);
  const [niveauEdite, setNiveauEdite] = useState<Niveau | null>(null);

  // Drawer (badge add/edit)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedValue, setSelectedValue] = useState('');
  const [selectedValue1, setSelectedValue1] = useState<string | null>(null);
  const [id, setId] = useState<number>(0);

  // Delete dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  // Role
  const [achievementWrite, setAchievementWrite] = useState(false);

  // ── Data state ────────────────────────────────────────────────────────────
  const [achievements, setAchievements] = useState<ActiveAchievement[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [niveaux, setNiveaux] = useState<Niveau[]>([]);
  const [niveauxErreur, setNiveauxErreur] = useState(false);
  const [usersByLevel, setUsersByLevel] = useState<Record<number, number>>({});
  /* Cumul de Tips gagnés par Spotter, lu sur le solde serveur (token_allocated). */
  const [cumuls, setCumuls] = useState<number[]>([]);

  // ── Reset form ────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormValues({
      achievement_name: { value: '', error: false, errorMessage: 'Please enter valid Achievement name' },
      achievement_description: { value: '', error: false, errorMessage: 'Please enter valid Achievement description' },
      achievement_criteria: { value: '', error: false, errorMessage: 'Please enter valid Achievement criteria' },
      level: { value: '', error: false, errorMessage: 'Please enter valid Achievement level' },
      gift_name: { value: '', error: false, errorMessage: 'Please enter valid Gift name' },
      no_of_tokens: { value: '', error: false, errorMessage: 'Please enter a valid number of Tips' },
    });
    setSelectedValue('');
    setSelectedValue1(null);
    setId(0);
    setEditingId(null);
  };

  const closeDrawer = () => { setDrawerOpen(false); resetForm(); };

  // ── Fetch badges ──────────────────────────────────────────────────────────
  const fetchAchievements = async () => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getAchievementsActive();
      if (Array.isArray(res)) {
        setAchievements(res.filter((r: any) => nonSupprime(r.delete_status)).map((r: any) => {
          const cadeau = r.gift_name && nonSupprime(r.gift_name.delete_status) ? r.gift_name : null;
          return {
            achievement_id: r.achievement_id,
            achievement_name: r.achievement_name,
            achievement_description: r.achievement_description,
            achievement_criteria: r.achievement_criteria,
            level: 'Level ' + r.level,
            gift_name_name: cadeau?.gift_name || '',
            no_of_tokens: r.no_of_tokens,
            tips_offerts: cadeau && cadeau.no_of_tokens != null ? Number(cadeau.no_of_tokens) : null,
          };
        }));
      }
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Badges illisibles pour le moment.'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  // ── Fetch categories (gifts) ──────────────────────────────────────────────
  const fetchCategory = async () => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    try {
      const res = await getgifts();
      if (Array.isArray(res)) setCategories(res.filter((g: any) => nonSupprime(g.delete_status)));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Cadeaux en Tips illisibles : aucun ne peut être lié à un badge.'), 'red'));
    }
  };

  // ── Référentiel des niveaux ───────────────────────────────────────────────
  const fetchNiveaux = async () => {
    try {
      setNiveaux(await getNiveaux());
      setNiveauxErreur(false);
    } catch (_) {
      setNiveauxErreur(true);
    }
  };

  // ── Spotters : niveau serveur et cumul de Tips gagnés ─────────────────────
  const fetchUsers = async () => {
    try {
      const res: any = await getUserdetails();
      const users: any[] = Array.isArray(res) ? res : [];
      const byLevel: Record<number, number> = {};
      users.forEach((u) => {
        const lvl = Number(u.current_level) || 1;
        byLevel[lvl] = (byLevel[lvl] || 0) + 1;
      });
      setUsersByLevel(byLevel);
      setCumuls(users.map((u) => Number(u.token_allocated) || 0));
    } catch (_) { /* noop */ }
  };

  // ── Fetch role ────────────────────────────────────────────────────────────
  const getRoleData = async () => {
    try {
      const res: any = await getUser();
      // Le droit s'appelle achievements_write côté admin-service (avec un s).
      setAchievementWrite(res?.role_type?.achievements_write ?? false);
    } catch (_) {
      dispatch(openSnackbar("Droits de l'administrateur illisibles : modification des niveaux et badges masquée.", 'red'));
    }
  };

  useEffect(() => {
    fetchAchievements();
    fetchCategory();
    fetchNiveaux();
    fetchUsers();
    getRoleData();
  }, []);

  // ── Badge CRUD ────────────────────────────────────────────────────────────
  const handleAddBadge = async () => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) { dispatch(openSnackbar('Session expirée', 'red')); return; }
    dispatch(loaderOn());
    try {
      const payload = {
        achievement_name: formValues.achievement_name.value,
        achievement_description: formValues.achievement_description.value,
        achievement_criteria: formValues.achievement_criteria.value,
        level: selectedValue,
        bonus_type: selectedValue,
        gift_name: { gift_id: id },
        no_of_tokens: formValues.no_of_tokens.value,
      };
      await createAchievement(payload);
      closeDrawer();
      fetchAchievements();
      dispatch(openSnackbar('Badge créé avec succès', 'green'));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Création du badge impossible.'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleDeleteBadge = async (achievementId: number) => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      await updateAchievements(achievementId, { delete_status: 'Yes' });
      fetchAchievements();
      dispatch(openSnackbar('Badge supprimé', 'green'));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Suppression du badge impossible.'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleEditBadgeOpen = async (achievementId: number) => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getAchievements(achievementId);
      setFormValues((prev: any) => ({
        ...prev,
        achievement_name: { ...prev.achievement_name, value: res.achievement_name || '' },
        achievement_description: { ...prev.achievement_description, value: res.achievement_description || '' },
        achievement_criteria: { ...prev.achievement_criteria, value: res.achievement_criteria || '' },
        no_of_tokens: { ...prev.no_of_tokens, value: String(res.no_of_tokens || '') },
        level: { ...prev.level, value: String(res.level || '') },
      }));
      setSelectedValue(String(res.level || ''));
      if (res.gift_name?.gift_id) {
        setSelectedValue1(String(res.gift_name.gift_id));
        setId(res.gift_name.gift_id);
      }
      setEditingId(achievementId);
      setDrawerOpen(true);
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Badge illisible.'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleUpdateBadge = async (achievementId: number) => {
    const jwtToken = sessionStorage.getItem('jwtToken');
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const payload = {
        achievement_name: formValues.achievement_name.value,
        achievement_description: formValues.achievement_description.value,
        achievement_criteria: formValues.achievement_criteria.value,
        level: selectedValue,
        bonus_type: selectedValue,
        gift_name: { gift_id: selectedValue1 ? parseInt(selectedValue1) : null },
        no_of_tokens: formValues.no_of_tokens.value,
      };
      await updateAchievements1(achievementId, payload);
      closeDrawer();
      fetchAchievements();
      dispatch(openSnackbar('Badge mis à jour', 'green'));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, 'Mise à jour du badge impossible.'), 'red'));
    } finally {
      dispatch(loaderOff());
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const critere = (label: string) => (
    <Typography sx={{ fontSize: 12, color: C.textSub }}>• {label}</Typography>
  );

  return (
    <Box sx={{ p: 3, minHeight: '100vh', background: C.bg }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <TitrePage domaine="reseau" aide={AIDE_RESEAU.reputation}>Réputation</TitrePage>
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: 0.5 }}>
            Niveaux attribués par le serveur, paliers de Tips cumulés et Tips offerts au palier
          </Typography>
        </Box>
        {achievementWrite && tab === ONGLET_BADGES && (
          <Button
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{
              background: C.accent,
              color: '#fff', borderRadius: '8px', textTransform: 'none',
              fontWeight: 600, px: 3, py: 1.2,
              boxShadow: "none",
              '&:hover': { boxShadow: "none" },
            }}
          >
            Nouveau badge
          </Button>
        )}
      </Box>

      {/* ── Tab container ──────────────────────────────────────────────────── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          TabIndicatorProps={{ style: { background: C.accentLight } }}
          sx={{ borderBottom: `1px solid ${C.border}`, px: 2 }}
        >
          <Tab
            label="Niveaux"
            sx={{ color: C.textSub, '&.Mui-selected': { color: C.accentLight }, textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label="Badges (paliers)"
            sx={{ color: C.textSub, '&.Mui-selected': { color: C.accentLight }, textTransform: 'none', fontWeight: 600 }}
          />
          <Tab
            label="Tips offerts au palier"
            sx={{ color: C.textSub, '&.Mui-selected': { color: C.accentLight }, textTransform: 'none', fontWeight: 600 }}
          />
        </Tabs>

        {/* ════════════════════════════════════════════════════════════════════
            Onglet Niveaux — référentiel serveur, lecture et édition
        ════════════════════════════════════════════════════════════════════ */}
        {tab === ONGLET_NIVEAUX && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontSize: 12, color: C.textMuted, mb: 2 }}>
              Chaque nuit, le serveur attribue à chaque Spotter le plus haut niveau actif dont il remplit tous
              les critères. Il n'y a pas de promotion manuelle.
            </Typography>
            {niveauxErreur && (
              <Typography sx={{ fontSize: 13, color: C.error, mb: 2 }}>
                Référentiel des niveaux illisible pour le moment.
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {niveaux.map((niv) => {
                const couleur = couleurNiveau(niv.niveau);
                const isExpanded = expandedLevel === niv.niveau;
                const count = usersByLevel[niv.niveau] || 0;
                const criteres: string[] = [];
                if (niv.minApprouves) criteres.push(`${niv.minApprouves}+ lieux validés`);
                if (niv.tauxApprobationMin) criteres.push(`Taux d'approbation ≥ ${niv.tauxApprobationMin} %`);
                if (niv.ancienneteJoursMin) criteres.push(`${niv.ancienneteJoursMin} jours d'ancienneté`);
                if (niv.categoriesMin) criteres.push(`${niv.categoriesMin} catégories distinctes`);
                return (
                  <Box
                    key={niv.niveau}
                    sx={{
                      background: 'rgba(31,30,27,0.02)',
                      border: `1px solid ${isExpanded ? couleur + '55' : C.border}`,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      opacity: niv.actif ? 1 : 0.7,
                      transition: 'border-color 0.2s',
                    }}
                  >
                    {/* Level header row */}
                    <Box
                      onClick={() => setExpandedLevel(isExpanded ? null : niv.niveau)}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2,
                        cursor: 'pointer',
                        '&:hover': { background: 'rgba(31,30,27,0.02)' },
                      }}
                    >
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        background: `${couleur}22`,
                        border: `2px solid ${couleur}66`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: couleur }}>{niv.niveau}</Typography>
                      </Box>

                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.text }}>{niv.nom}</Typography>
                          <Box sx={{
                            background: niv.actif ? `${C.success}22` : 'rgba(31,30,27,0.06)',
                            color: niv.actif ? C.success : C.textMuted,
                            border: `1px solid ${niv.actif ? C.success + '44' : C.border}`,
                            borderRadius: '6px', px: 1, py: 0.2, fontSize: 11, fontWeight: 700,
                          }}>
                            {niv.actif ? 'Actif' : 'Inactif'}
                          </Box>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                          {criteres.length ? criteres.join(' · ') : "Niveau d'entrée — aucun critère"}
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: 'right' }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 700, color: couleur }}>{count}</Typography>
                        <Typography sx={{ fontSize: 10, color: C.textMuted }}>Spotters</Typography>
                      </Box>
                      <Typography sx={{ fontSize: 14, color: C.textMuted, ml: 1 }}>
                        {isExpanded ? '▲' : '▼'}
                      </Typography>
                    </Box>

                    {/* Expanded content */}
                    {isExpanded && (
                      <Box sx={{ px: 3, pb: 3, borderTop: `1px solid ${C.border}` }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                          <Box>
                            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                              Avantages
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: C.text }}>{niv.avantages || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                              Critères d'accès à ce niveau
                            </Typography>
                            {criteres.length === 0 ? (
                              <Typography sx={{ fontSize: 12, color: C.textMuted, fontStyle: 'italic' }}>Aucun critère — niveau de départ</Typography>
                            ) : (
                              <Box>{criteres.map((c) => <React.Fragment key={c}>{critere(c)}</React.Fragment>)}</Box>
                            )}
                            {!niv.actif && (
                              <Typography sx={{ fontSize: 12, color: C.warning, mt: 0.5 }}>
                                Inactif : jamais attribué par le job de nuit.
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {achievementWrite && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${C.border}` }}>
                            <Button
                              size="small"
                              startIcon={<CreateIcon sx={{ fontSize: 14 }} />}
                              onClick={(e) => { e.stopPropagation(); setNiveauEdite(niv); }}
                              sx={{
                                background: `${couleur}22`, color: couleur,
                                border: `1px solid ${couleur}44`,
                                borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: 12,
                                '&:hover': { background: `${couleur}33` },
                              }}
                            >
                              Modifier les critères
                            </Button>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            Onglet Badges — paliers de Tips cumulés
        ════════════════════════════════════════════════════════════════════ */}
        {tab === ONGLET_BADGES && (
          <Box sx={{ p: 3 }}>
            {achievements.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ fontSize: 36, mb: 1 }}>🏅</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text, mb: 0.5 }}>
                  Aucun badge actif
                </Typography>
                <Typography sx={{ fontSize: 13, color: C.textMuted }}>
                  Créez un badge pour poser un palier de Tips cumulés
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
                {achievements.map((badge) => {
                  const lvlNum = parseInt(badge.level.replace('Level ', '')) || 1;
                  const lvlColor = couleurNiveau(lvlNum);
                  const seuil = Number(badge.no_of_tokens) || 0;
                  const auSeuil = cumuls.filter((c) => c >= seuil).length;

                  return (
                    <Box
                      key={badge.achievement_id}
                      sx={{
                        background: 'rgba(31,30,27,0.02)',
                        border: `1px solid ${C.border}`,
                        borderRadius: '8px',
                        p: 2.5,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        transition: 'border-color 0.2s',
                        '&:hover': { borderColor: C.borderAccent },
                      }}
                    >
                      {/* Card header: icon + name + actions */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Box sx={{
                          width: 48, height: 48, borderRadius: '8px',
                          background: `${lvlColor}22`, border: `1px solid ${lvlColor}44`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 22, flexShrink: 0,
                        }}>
                          {/* Display emoji from name or fallback */}
                          {badge.achievement_name.match(/^\p{Emoji}/u)?.[0] || '🏅'}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>
                            {badge.achievement_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                            <Box sx={{
                              background: `${lvlColor}22`, color: lvlColor,
                              border: `1px solid ${lvlColor}44`,
                              borderRadius: '4px', px: 0.75, py: 0.1, fontSize: 10, fontWeight: 700,
                            }}>
                              {niveaux.find((n) => n.niveau === lvlNum)?.nom ?? badge.level}
                            </Box>
                          </Box>
                        </Box>
                        {achievementWrite && (
                          <Box sx={{ display: 'flex', gap: 0.25, flexShrink: 0 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditBadgeOpen(badge.achievement_id)}
                              sx={{ color: C.accentLight, '&:hover': { background: C.accentMuted } }}
                            >
                              <CreateIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => { setDeleteTargetId(badge.achievement_id); setOpenDialog(true); }}
                              sx={{ color: C.error, '&:hover': { background: 'rgba(162,58,13,0.1)' } }}
                            >
                              <DeleteIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Box>
                        )}
                      </Box>

                      {/* Description */}
                      {badge.achievement_description && (
                        <Typography sx={{ fontSize: 12, color: C.textMuted, lineHeight: 1.5 }}>
                          {badge.achievement_description}
                        </Typography>
                      )}

                      {/* Criteria */}
                      {badge.achievement_criteria && (
                        <Box sx={{ background: 'rgba(31,30,27,0.03)', border: `1px solid ${C.border}`, borderRadius: '8px', px: 1.5, py: 1 }}>
                          <Typography sx={{ fontSize: 10, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.25 }}>Critère</Typography>
                          <Typography sx={{ fontSize: 12, color: C.textSub }}>{badge.achievement_criteria}</Typography>
                        </Box>
                      )}

                      {/* Palier : seuil et Tips offerts */}
                      <Box sx={{ background: 'rgba(124,86,0,0.06)', border: '1px solid rgba(124,86,0,0.2)', borderRadius: '8px', px: 1.5, py: 1 }}>
                        <Typography sx={{ fontSize: 12, color: C.textSub }}>
                          Dès <strong style={{ color: C.text }}>{seuil.toLocaleString('fr-FR')} Tips</strong> cumulés
                          {badge.tips_offerts != null
                            ? <> : <strong style={{ color: C.warning }}>{badge.tips_offerts.toLocaleString('fr-FR')} Tips offerts</strong> ({badge.gift_name_name})</>
                            : ' : aucun cadeau en Tips lié, rien n’est offert'}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.25 }}>
                          {auSeuil} Spotter{auSeuil !== 1 ? 's' : ''} au-dessus du seuil
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ════════════════════════════════════════════════════════════════════
            Onglet Tips offerts au palier (ancien écran /gifts, option 3)
        ════════════════════════════════════════════════════════════════════ */}
        {tab === ONGLET_TIPS_OFFERTS && (
          <Box sx={{ p: 3 }}>
            <Gifts integre />
          </Box>
        )}
      </Box>

      {/* ── Note banner ────────────────────────────────────────────────────── */}
      <Box sx={{ mt: 3, px: 3, py: 2, background: C.accentMuted, border: `1px solid ${C.borderAccent}`, borderRadius: '8px' }}>
        <Typography sx={{ fontSize: 12, color: C.textSub }}>
          <Box component="span" sx={{ color: C.accentLight, fontWeight: 700 }}>ℹ Paliers</Box>
          {' '}— Quand le cumul de Tips gagnés d'un Spotter atteint le seuil d'un badge, le serveur crédite
          une seule fois les Tips du cadeau lié. Le niveau ne multiplie pas les Tips.
        </Typography>
      </Box>

      {/* ── Delete dialog ──────────────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => {
          if (deleteTargetId !== null) handleDeleteBadge(deleteTargetId);
          setOpenDialog(false);
        }}
        contentText="Confirmer la suppression de ce badge ?"
      />

      {/* ── Add / Edit Drawer ──────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: 440, background: C.card, borderLeft: `1px solid ${C.border}` } }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Drawer header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
              {editingId ? 'Modifier le badge' : 'Nouveau badge'}
            </Typography>
            <IconButton onClick={closeDrawer} sx={{ color: C.textMuted }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Fields */}
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Name */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Nom du badge
              </Typography>
              <TextField
                fullWidth size="small"
                placeholder="ex: 🏅 Super Spotter"
                value={formValues.achievement_name.value}
                onChange={(e) => setFormValues((prev: any) => ({ ...prev, achievement_name: { ...prev.achievement_name, value: e.target.value } }))}
                sx={fieldSx}
              />
            </Box>

            {/* Description */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Description
              </Typography>
              <TextField
                fullWidth size="small" multiline rows={2}
                value={formValues.achievement_description.value}
                onChange={(e) => setFormValues((prev: any) => ({ ...prev, achievement_description: { ...prev.achievement_description, value: e.target.value } }))}
                sx={fieldSx}
              />
            </Box>

            {/* Critère */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Critère de déclenchement
              </Typography>
              <TextField
                fullWidth size="small"
                value={formValues.achievement_criteria.value}
                onChange={(e) => setFormValues((prev: any) => ({ ...prev, achievement_criteria: { ...prev.achievement_criteria, value: e.target.value } }))}
                sx={fieldSx}
              />
            </Box>

            {/* Level select */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Niveau
              </Typography>
              <Select
                fullWidth size="small" value={selectedValue} displayEmpty
                onChange={(e) => setSelectedValue(e.target.value as string)}
                sx={selectSx}
                MenuProps={{ PaperProps: { sx: { background: 'var(--c-surface)', color: C.text } } }}
              >
                <MenuItem value="" disabled sx={{ color: C.textMuted }}>Sélectionner un niveau</MenuItem>
                {niveaux.map((n) => (
                  <MenuItem key={n.niveau} value={String(n.niveau)} sx={{ color: couleurNiveau(n.niveau) }}>
                    Niveau {n.niveau} — {n.nom}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Gift select */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Cadeau en Tips offert au palier
              </Typography>
              <Select
                fullWidth size="small" value={selectedValue1 ?? ''} displayEmpty
                onChange={(e) => {
                  setSelectedValue1(e.target.value as string);
                  setId(parseInt(e.target.value as string));
                }}
                sx={selectSx}
                MenuProps={{ PaperProps: { sx: { background: 'var(--c-surface)', color: C.text } } }}
              >
                <MenuItem value="" disabled sx={{ color: C.textMuted }}>Sélectionner un cadeau en Tips</MenuItem>
                {categories.map((cat: any) => (
                  <MenuItem key={cat.gift_id} value={String(cat.gift_id)} sx={{ color: C.text }}>
                    {cat.gift_name} — {cat.no_of_tokens} Tips offerts
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Tips */}
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textSub, mb: 0.75, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Seuil de Tips cumulés
              </Typography>
              <TextField
                fullWidth size="small" type="number"
                value={formValues.no_of_tokens.value}
                onChange={(e) => setFormValues((prev: any) => ({ ...prev, no_of_tokens: { ...prev.no_of_tokens, value: e.target.value } }))}
                sx={fieldSx}
              />
              <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.5 }}>
                Cumul de Tips gagnés à partir duquel le serveur crédite, une fois, les Tips du cadeau lié.
              </Typography>
            </Box>
          </Box>

          {/* Drawer footer */}
          <Box sx={{ display: 'flex', gap: 2, pt: 3, borderTop: `1px solid ${C.border}`, mt: 2 }}>
            <Button
              fullWidth onClick={closeDrawer}
              sx={{ color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { background: 'rgba(31,30,27,0.04)' } }}
            >
              Annuler
            </Button>
            <Button
              fullWidth
              onClick={() => { if (editingId) handleUpdateBadge(editingId); else handleAddBadge(); }}
              sx={{
                background: C.accent,
                color: '#fff', borderRadius: '8px', textTransform: 'none', fontWeight: 600,
                boxShadow: "none",
                '&:hover': { boxShadow: "none" },
              }}
            >
              {editingId ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Édition d'un niveau ────────────────────────────────────────────── */}
      <NiveauDialog niveau={niveauEdite} onClose={() => setNiveauEdite(null)} onSaved={fetchNiveaux} />
    </Box>
  );
}
